/**
 * Rule evaluation logic for ludicrous-gateway
 * Evaluates routing rules against request analysis
 */

import type { CompiledRoutingRule, ContentAnalysis } from "../types/routing.js";

/**
 * Result of evaluating a single rule
 */
export interface RuleEvaluationResult {
  /** Whether all conditions matched */
  matched: boolean;
  /** Which conditions matched */
  matchedConditions: string[];
  /** Which conditions failed */
  failedConditions: string[];
}

/**
 * Evaluate a single rule against content analysis
 */
export function evaluateRule(
  rule: CompiledRoutingRule,
  analysis: ContentAnalysis
): RuleEvaluationResult {
  const matchedConditions: string[] = [];
  const failedConditions: string[] = [];

  // If rule is disabled, it doesn't match
  if (!rule.enabled) {
    return {
      matched: false,
      matchedConditions: [],
      failedConditions: ["rule_disabled"],
    };
  }

  let hasConditions = false;

  // Check keywords
  if (rule.keywords.size > 0) {
    hasConditions = true;
    const keywordMatched = checkKeywords(
      rule.keywords,
      analysis.matchedKeywords
    );

    if (keywordMatched) {
      matchedConditions.push("keywords");
    } else {
      failedConditions.push("keywords");
    }
  }

  // Check patterns
  if (rule.patterns.length > 0) {
    hasConditions = true;
    const patternMatched = checkPatterns(
      rule.patterns,
      analysis.matchedPatterns
    );

    if (patternMatched) {
      matchedConditions.push("patterns");
    } else {
      failedConditions.push("patterns");
    }
  }

  // Check minimum tokens
  if (rule.minTokens !== undefined) {
    hasConditions = true;
    if (analysis.estimatedTokens >= rule.minTokens) {
      matchedConditions.push("min_tokens");
    } else {
      failedConditions.push("min_tokens");
    }
  }

  // Check maximum tokens
  if (rule.maxTokens !== undefined) {
    hasConditions = true;
    if (analysis.estimatedTokens <= rule.maxTokens) {
      matchedConditions.push("max_tokens");
    } else {
      failedConditions.push("max_tokens");
    }
  }

  // Check model whitelist
  if (rule.models.size > 0) {
    hasConditions = true;
    if (rule.models.has(analysis.requestedModel)) {
      matchedConditions.push("models");
    } else {
      failedConditions.push("models");
    }
  }

  // Check has_tools condition
  if (rule.hasTools !== undefined) {
    hasConditions = true;
    const requestHasTools = analysis.hasTools || analysis.hasToolUseBlocks;
    console.log(`[DEBUG] Rule ${rule.name}: rule.hasTools=${rule.hasTools}, requestHasTools=${requestHasTools}, analysis.hasTools=${analysis.hasTools}`);
    if (rule.hasTools === requestHasTools) {
      matchedConditions.push("has_tools");
    } else {
      failedConditions.push("has_tools");
    }
  } else if (rule.name === 'tool-use-routing') {
    console.log(`[DEBUG] Rule ${rule.name}: hasTools is UNDEFINED, rule object keys:`, Object.keys(rule));
  }

  // Rule matches if it has conditions and all passed
  const matched = hasConditions && failedConditions.length === 0;

  return {
    matched,
    matchedConditions,
    failedConditions,
  };
}

/**
 * Check if any rule keywords match analysis keywords
 */
function checkKeywords(
  ruleKeywords: Set<string>,
  analysisKeywords: string[]
): boolean {
  // Convert analysis keywords to lowercase for comparison
  const lowerAnalysisKeywords = analysisKeywords.map((k) => k.toLowerCase());

  for (const keyword of ruleKeywords) {
    // Check if keyword is contained in any analysis keyword
    for (const analysisKeyword of lowerAnalysisKeywords) {
      if (
        analysisKeyword.includes(keyword) ||
        keyword.includes(analysisKeyword)
      ) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Check if any rule patterns match analysis patterns
 */
function checkPatterns(
  rulePatterns: RegExp[],
  analysisPatterns: string[]
): boolean {
  // The analysis already has matched patterns as strings (regex sources)
  // A rule pattern matches if its source is in the analysis patterns
  for (const pattern of rulePatterns) {
    if (analysisPatterns.includes(pattern.source)) {
      return true;
    }
  }

  return false;
}

/**
 * Find the first matching rule from a sorted list
 */
export function findMatchingRule(
  rules: CompiledRoutingRule[],
  analysis: ContentAnalysis
): { rule: CompiledRoutingRule; evaluation: RuleEvaluationResult } | null {
  for (const rule of rules) {
    const evaluation = evaluateRule(rule, analysis);

    if (evaluation.matched) {
      return { rule, evaluation };
    }
  }

  return null;
}

/**
 * Evaluate all rules and return detailed results
 * Useful for debugging routing decisions
 */
export function evaluateAllRules(
  rules: CompiledRoutingRule[],
  analysis: ContentAnalysis
): Array<{
  rule: CompiledRoutingRule;
  evaluation: RuleEvaluationResult;
}> {
  return rules.map((rule) => ({
    rule,
    evaluation: evaluateRule(rule, analysis),
  }));
}
